import * as React from 'react';
import styled from 'styled-components';
import {
  useFormContext,
  Controller,
  useController,
} from 'react-hook-form';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import {
  InputBaseContainer,
  InputSelectWithLabel,
  InputTextWithLabel,
  LoaderWithContainer,
  ModalDefaultDraggable,
} from '../molecules';
// import { getFranchiseList } from '../api/franchise';
import { getJurisdictions } from '../api/jurisdiction';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { ButtonMain } from '../atoms';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { getUsers, getUsersCustom } from '../api/users';
import { USER_TYPES_VALUES } from '../constants/forms';
import { stepThreeLabels } from './ProjectFormContainer/ProjectFormLabels';
import { RootState } from '../redux/root';
import { getCurrentUser } from '../api/userSettings';
import useUserRole from '../hooks/useUserRole';
import LeadRequesterCard from '../organisms/LeadRequesterCard';

const ProjectFormStepThree = ({
  currentValidationSchema,
  setIsSecondModalOpen,
  setIsSomeStepLoading,
  isContactFormSubmitting,
  setIsContactFormSubmitting,
  selectedProject,
}: Props) => {
  const {
    control,
    errors,
    setValue,
    getValues,
  } = useFormContext();
  const { isEmployee } = useUserRole();
  const labels = stepThreeLabels;
  const [isLoading, setIsLoading] = React.useState<boolean>(
    false,
  );

  const [
    jurisdictionsData,
    setJurisdictionsData,
  ] = React.useState([]);

  const PROJECT_TYPE_OPTIONS = [
    { value: '1_2_FAMILY', label: '1 & 2 Family Dwelling' },
    { value: 'MULTIFAMILY', label: 'Multifamily' },
    { value: 'COMMERCIAL', label: 'Commercial' },
  ];

  const projectCandidate = useSelector((s:RootState) => s.projectsSlice.projectCandidate);
  const leadRequester = useSelector((s:RootState) => s.projectsSlice.projectImport?.requester);
  const [
    projectManagersData,
    setProjectManagersData,
  ] = React.useState([]);
  const [contactData, setContactsData] = React.useState([]);
  useEffect(() => {
    if (projectCandidate) {
      // @ts-ignore
      const juri = jurisdictionsData.find(j => j.id === projectCandidate.jurisdiction);
      if (juri) {
        setValue('jurisdiction', juri);
      }

      setValue('projectContacts', [projectCandidate.contact]);
    }
  }, [projectCandidate, jurisdictionsData]);

  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = React.useState(false);
  const [jurisdictionMenuOpen, setJurisdictionMenuOpen] = React.useState(false);

  const getJurisdictionsData = () => {
    if (jurisdictionsData.length) {
      setJurisdictionMenuOpen(true);
      return;
    }
    setIsJurisdictionsLoading(true);
    getJurisdictions({
      ...GET_ALL_QUERY_PARAMS,
      ordering: 'name',
    }).then(res => {
      setJurisdictionsData(res.data.results);
      setJurisdictionMenuOpen(true);
    }).finally(() => {
      setIsJurisdictionsLoading(false);
    });
  };

  const [isManagersLoading, setIsManagersLoading] = React.useState(false);

  const getManagersData = () => {
    if (projectManagersData.length) return;
    setIsManagersLoading(true);
    Promise.all([
      getUsersCustom({
        userRole: USER_TYPES_VALUES.EMPLOYEE,
        project: 'true',
      }),
      getCurrentUser(),
    ]).then(([projectManagersResponse, currentUserResponse]) => {
      const activeManagers = projectManagersResponse.data?.filter(
        el => el?.isActive,
      );
      setProjectManagersData(activeManagers);

      if (isEmployee && activeManagers) {
        const managerValue = getValues('manager');
        if (!managerValue) {
          const currentUserAsManager = activeManagers.find(
            el => el.id === currentUserResponse.data.id,
          );
          if (currentUserAsManager) {
            setValue('manager', currentUserAsManager);
          }
        }
      }
    })
    .catch(() => {})
    .finally(() => {
      setIsManagersLoading(false);
      setIsContactFormSubmitting(false);
    });
  };

  useEffect(() => {
    getManagersData();
  }, []);

  const [isContactsLoading, setIsContactsLoading] = React.useState(false);
  const [isContactSelectModalOpen, setIsContactSelectModalOpen] = React.useState(false);
  const [contactSearchValue, setContactSearchValue] = React.useState('');
  const contactSearchTimer = React.useRef<any>(null);

  const handleCloseContactSelectModal = () => {
    setIsContactSelectModalOpen(false);
    setContactsData([]);
    setContactSearchValue('');
  };

  const searchContacts = (inputValue: string) => {
    if (contactSearchTimer.current) {
      clearTimeout(contactSearchTimer.current);
    }
    if (!inputValue || inputValue.length < 2) {
      setContactsData([]);
      return;
    }
    contactSearchTimer.current = setTimeout(() => {
      setIsContactsLoading(true);
      getUsersCustom({
        userRole: USER_TYPES_VALUES.CLIENT,
        project: 'true',
        search: inputValue,
      }).then(response => {
        setContactsData(
          response.data?.filter(el => el?.isActive) || [],
        );
      })
      .catch(() => {})
      .finally(() => setIsContactsLoading(false));
    }, 300);
  };

  return (
    <>
      <InputBaseContainer>
        <Controller
          as={InputTextWithLabel}
          name="name"
          control={control}
          helperText={errors.name?.message}
          error={errors.name?.message}
          label={labels.name}
          isRequired={isYupFieldRequired(
            'name',
            currentValidationSchema,
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          name="jurisdiction"
          defaultValue=""
          control={control}
          render={props => {
            return (
              <InputSelectWithLabel
                placeholder=""
                isLoading={isJurisdictionsLoading}
                isDisabled={isJurisdictionsLoading}
                label={labels.jurisdiction}
                helperText={errors.jurisdiction?.message}
                error={errors.jurisdiction?.message}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                isRequired={isYupFieldRequired(
                  'jurisdiction',
                  currentValidationSchema,
                )}
                onFocus={() => getJurisdictionsData()}
                menuIsOpen={jurisdictionMenuOpen}
                onMenuClose={() => setJurisdictionMenuOpen(false)}
                options={jurisdictionsData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            );
          }}
        />
      </InputBaseContainer>
      {/* <InputBaseContainer>
        <Controller
          name="franchise"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              placeholder=""
              isLoading={isLoading}
              isDisabled={isLoading}
              label={labels.franchise}
              helperText={errors.franchise?.message}
              error={errors.franchise?.message}
              getOptionValue={(item: any) =>
                item.id || Math.random()
              }
              getOptionLabel={(item: any) => item.name}
              isRequired={isYupFieldRequired(
                'franchise',
                currentValidationSchema,
              )}
              options={franchisesData}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
          )}
        />
      </InputBaseContainer> */}
      <InputBaseContainer>
        <Controller
          name="manager"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              placeholder=""
              isLoading={isManagersLoading}
              isDisabled={isManagersLoading}
              label={labels.manager}
              onFocus={() => getManagersData()}
              helperText={errors.manager?.message}
              error={errors.manager?.message}
              getOptionValue={(item: any) =>
                item.id || Math.random()
              }
              getOptionLabel={(item: any) =>
                `${item.firstName} ${item.lastName}`
              }
              isRequired={isYupFieldRequired(
                'manager',
                currentValidationSchema,
              )}
              options={projectManagersData}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
          )}
        />
      </InputBaseContainer>
      <Controller
        name="projectContacts"
        defaultValue=""
        control={control}
        render={({ onChange, value }) => (
          <>
            {leadRequester && (
              <LeadRequesterCard
                requester={leadRequester}
                contacts={Array.isArray(value) ? value : []}
                setContacts={onChange}
                individualsOnly
              />
            )}
            <ContactPickerSection>
              <ContactPickerLabel>
                {labels.contacts}
                {isYupFieldRequired('contacts', currentValidationSchema) && (
                  <span style={{ color: 'var(--danger-500)', marginLeft: '2px' }}>*</span>
                )}
              </ContactPickerLabel>
              {(!Array.isArray(value) || value.length === 0) ? (
                <EmptyContactsCTA
                  type="button"
                  onClick={() => setIsContactSelectModalOpen(true)}
                  hasError={!!errors.contacts?.message}
                >
                  <CTAIconWrap>
                    <PersonAddIcon style={{ fontSize: 22 }} />
                  </CTAIconWrap>
                  <CTAText>
                    <CTATitle>Add Contact</CTATitle>
                    <CTASubtitle>Tap here to search or add new contacts for this project</CTASubtitle>
                  </CTAText>
                </EmptyContactsCTA>
              ) : (
                <SelectedContactsBlock>
                  <SelectedHeader>
                    <SelectedLabel>Selected ({value.length})</SelectedLabel>
                    <AddMoreButton
                      type="button"
                      onClick={() => setIsContactSelectModalOpen(true)}
                    >
                      <PersonAddIcon style={{ fontSize: 16 }} />
                      Add Contact
                    </AddMoreButton>
                  </SelectedHeader>
                  <SelectedChips>
                    {value.map(c => {
                      const chipName = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.companyName || c.email;
                      return (
                        <Chip key={c.id}>
                          {chipName}
                          <ChipRemove onClick={() => onChange(value.filter(sc => sc.id !== c.id))}>&times;</ChipRemove>
                        </Chip>
                      );
                    })}
                  </SelectedChips>
                </SelectedContactsBlock>
              )}
              {errors.contacts?.message && (
                <ContactPickerError>{errors.contacts.message}</ContactPickerError>
              )}
            </ContactPickerSection>
            {isContactSelectModalOpen && (
              <ModalDefaultDraggable
                title="Select Contact"
                onClose={handleCloseContactSelectModal}
                centered
              >
                <ContactSelectPopupBody>
                  <ContactSearchInput
                    placeholder="Search contacts by name or email (min 2 characters)..."
                    value={contactSearchValue}
                    onChange={e => {
                      setContactSearchValue(e.target.value);
                      searchContacts(e.target.value);
                    }}
                    autoFocus
                  />
                  {isContactsLoading && (
                    <ContactPickerStatus>Searching...</ContactPickerStatus>
                  )}
                  {!isContactsLoading && contactSearchValue.length >= 2 && contactData && contactData.length > 0 && (
                    <ContactResultsList>
                      {contactData.map(contact => {
                        const isSelected = Array.isArray(value) && value.some(c => c.id === contact.id);
                        const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
                        const detail = [contact.email, contact.companyName || contact.parentCompany?.companyName].filter(Boolean).join(' - ');
                        return (
                          <ContactResultItem
                            key={contact.id}
                            $selected={isSelected}
                            onClick={() => {
                              const current = Array.isArray(value) ? value : [];
                              if (isSelected) {
                                onChange(current.filter(c => c.id !== contact.id));
                              } else {
                                onChange([...current, contact]);
                              }
                            }}
                          >
                            <ContactCheckbox $checked={isSelected}>{isSelected && '✓'}</ContactCheckbox>
                            <ContactInfo>
                              <ContactName>{name || contact.companyName || 'Unknown'}</ContactName>
                              {detail && <ContactDetail>{detail}</ContactDetail>}
                            </ContactInfo>
                          </ContactResultItem>
                        );
                      })}
                    </ContactResultsList>
                  )}
                  {!isContactsLoading && contactSearchValue.length >= 2 && contactData && contactData.length === 0 && (
                    <ContactPickerStatus>No contacts found</ContactPickerStatus>
                  )}
                  {Array.isArray(value) && value.length > 0 && (
                    <SelectedChipsWrap>
                      <SelectedLabel>Selected ({value.length}):</SelectedLabel>
                      <SelectedChips>
                        {value.map(c => {
                          const chipName = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.companyName || c.email;
                          return (
                            <Chip key={c.id}>
                              {chipName}
                              <ChipRemove onClick={() => onChange(value.filter(sc => sc.id !== c.id))}>&times;</ChipRemove>
                            </Chip>
                          );
                        })}
                      </SelectedChips>
                    </SelectedChipsWrap>
                  )}
                  <NoContactPrompt>
                    Can&apos;t find your contact?
                    <AddNewContactLink
                      type="button"
                      onClick={() => {
                        handleCloseContactSelectModal();
                        setIsSecondModalOpen(true);
                      }}
                    >
                      Add new contact
                    </AddNewContactLink>
                  </NoContactPrompt>
                  <ContactSelectActions>
                    <ButtonMain color="primary" onClick={handleCloseContactSelectModal}>
                      Done
                    </ButtonMain>
                  </ContactSelectActions>
                </ContactSelectPopupBody>
              </ModalDefaultDraggable>
            )}
          </>
        )}
      />
      <ProjectTypeContainer hasError={!!errors.project_type}>
        <Controller
          name="project_type"
          control={control}
          defaultValue={null}
          render={props => (
            <>
              <ProjectTypeLabel>
                Project Type
                {isYupFieldRequired('project_type', currentValidationSchema) && (
                  <RequiredMark>*</RequiredMark>
                )}
              </ProjectTypeLabel>
              <RadioGroup>
                {[
                  { value: '1_2_FAMILY', label: '1 & 2 Family Dwelling' },
                  { value: 'MULTIFAMILY', label: 'Multifamily' },
                  { value: 'COMMERCIAL', label: 'Commercial' },
                ].map(option => (
                  <RadioOption
                    key={option.value}
                    isSelected={props.value === option.value}
                    onClick={() => props.onChange(option.value)}
                  >
                    <RadioCircle isSelected={props.value === option.value}>
                      {props.value === option.value && <RadioDot />}
                    </RadioCircle>
                    <RadioLabel>{option.label}</RadioLabel>
                  </RadioOption>
                ))}
              </RadioGroup>
              {errors.project_type && (
                <ProjectTypeError>Please select a project type</ProjectTypeError>
              )}
            </>
          )}
        />
      </ProjectTypeContainer>

    </>
  );
};

const ProjectTypeContainer = styled.div<{ hasError: boolean }>`
  padding: var(--space-4) 0;
  width: 100%;
  ${({ hasError }) => hasError && `
    padding-bottom: var(--space-2);
  `}
`;

const ProjectTypeLabel = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
  margin-bottom: var(--space-3);
`;

const RequiredMark = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const RadioOption = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid ${({ isSelected }) =>
    isSelected ? 'var(--primary-600)' : 'var(--neutral-300)'};
  background: ${({ isSelected }) =>
    isSelected ? 'var(--primary-50)' : 'var(--neutral-0)'};
  cursor: pointer;
  transition:
    border-color var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    border-color: ${({ isSelected }) =>
      isSelected ? 'var(--primary-600)' : 'var(--neutral-400)'};
    background: ${({ isSelected }) =>
      isSelected ? 'var(--primary-50)' : 'var(--neutral-50)'};
  }
`;

const RadioCircle = styled.div<{ isSelected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: var(--radius-full);
  border: 2px solid ${({ isSelected }) =>
    isSelected ? 'var(--primary-600)' : 'var(--neutral-400)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color var(--motion-fast) var(--ease-standard);
`;

const RadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: var(--primary-600);
`;

const RadioLabel = styled.span`
  font-size: var(--fs-base);
  color: var(--neutral-800);
  white-space: nowrap;
`;

const ProjectTypeError = styled.div`
  color: var(--danger-500);
  font-size: var(--fs-sm);
  margin-top: var(--space-2);
`;

const ContactPickerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  width: 100%;
`;

const ContactPickerLabel = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
`;

const EmptyContactsCTA = styled.button<{ hasError: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-4);
  background: var(--primary-50);
  border: 2px dashed ${({ hasError }) =>
    hasError ? 'var(--danger-500)' : 'var(--primary-400)'};
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    background-color var(--motion-fast) var(--ease-standard),
    border-color var(--motion-fast) var(--ease-standard),
    transform var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--primary-100);
    border-color: var(--primary-600);
  }
  &:active {
    transform: scale(0.995);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const CTAIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background: var(--primary-600);
  color: var(--neutral-0);
  flex-shrink: 0;
`;

const CTAText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CTATitle = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--primary-800);
`;

const CTASubtitle = styled.div`
  font-size: var(--fs-sm);
  color: var(--primary-700);
`;

const SelectedContactsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--neutral-50);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
`;

const SelectedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
`;

const AddMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: var(--neutral-0);
  border: 1px solid var(--primary-400);
  border-radius: 8px;
  color: var(--primary-700);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  font-family: inherit;
  cursor: pointer;
  transition:
    background-color var(--motion-fast) var(--ease-standard),
    border-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ContactSearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  font-size: var(--fs-base);
  font-family: inherit;
  color: var(--neutral-800);
  background: var(--neutral-0);
  transition: border-color var(--motion-fast) var(--ease-standard),
    box-shadow var(--motion-fast) var(--ease-standard);

  &::placeholder { color: var(--neutral-400); }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const ContactPickerStatus = styled.div`
  text-align: center;
  color: var(--neutral-500);
  font-size: var(--fs-sm);
  padding: var(--space-2) 0;
`;

const ContactResultsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
`;

const ContactResultItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  border-bottom: 1px solid var(--neutral-100);
  background: ${({ $selected }) => $selected ? 'var(--primary-50)' : 'var(--neutral-0)'};
  transition: background-color var(--motion-fast) var(--ease-standard);
  &:last-child { border-bottom: 0; }
  &:hover { background: ${({ $selected }) => $selected ? 'var(--primary-100)' : 'var(--neutral-50)'}; }
`;

const ContactCheckbox = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: var(--radius-sm);
  border: 2px solid ${({ $checked }) => $checked ? 'var(--primary-600)' : 'var(--neutral-400)'};
  background: ${({ $checked }) => $checked ? 'var(--primary-600)' : 'transparent'};
  color: var(--neutral-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ContactName = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContactDetail = styled.div`
  font-size: var(--fs-sm);
  color: var(--neutral-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedChipsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SelectedLabel = styled.div`
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--neutral-600);
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-full);
  font-size: var(--fs-sm);
  color: var(--primary-800);
`;

const ChipRemove = styled.span`
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: var(--primary-400);
  margin-left: var(--space-1);
  &:hover { color: var(--danger-500); }
`;

const ContactPickerError = styled.div`
  color: var(--danger-500);
  font-size: var(--fs-sm);
`;

const ContactSelectPopupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  min-width: 480px;
`;

const NoContactPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3) 0;
  font-size: var(--fs-sm);
  color: var(--neutral-600);
  border-top: 1px solid var(--neutral-200);
`;

const AddNewContactLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: var(--fw-semibold);
  color: var(--primary-600);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-sm);
  }
`;

const ContactSelectActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-2);
`;

interface Props {
  currentValidationSchema: any;
  setIsSecondModalOpen: (data: boolean) => void;
  isContactFormSubmitting: boolean;
  setIsSomeStepLoading: (data: boolean) => void;
  setIsContactFormSubmitting: (data: boolean) => void;
  selectedProject?: any;
}

interface FieldsType {
  // franchise: string;
  jurisdiction: string;
  manager: string;
  contacts: string;
  name: string;
}

export default ProjectFormStepThree;
